import * as Chart from 'chart.js';
import * as React from 'react';
import { Line } from 'react-chartjs-2';
import { ChartWrapper } from '../ChartWrapper';
import { DefaultChartProps, getOptionsForDefaultChartOptions } from '../DefaultChartProps';

export interface TimeLineChartDataItem {
    label: string;
    values: Array<{ x: Date; y: number }>;
    colour: string;
}

export interface TimeLineChartData {
    lines: TimeLineChartDataItem[];
}

export interface TimeLineChartProps extends DefaultChartProps {
    data: TimeLineChartData;

    labels?: Chart.ChartData['labels'];

    hideGrid?: boolean | 'x' | 'y';
    xAxisUnit?: Chart.TimeUnit;

    yStartsAtZero?: boolean;
}

export function TimeLineChart({
    data,
    hideGrid = false,
    labels,
    options,
    otherOptions,
    xAxisUnit,
    yStartsAtZero,
    ...props
}: TimeLineChartProps): JSX.Element {
    const xAxes: Chart.ChartXAxe = {
        time: {
            tooltipFormat: 'DD/MM/YY',
        },
        type: 'time',
    };
    const yAxes: Chart.ChartYAxe = {};

    if (xAxisUnit) {
        xAxes.time = {
            ...xAxes.time,
            unit: xAxisUnit,
        };
    }

    if (hideGrid) {
        if (hideGrid === 'x' || hideGrid === true) {
            xAxes.gridLines = {
                ...xAxes.gridLines,
                display: false,
            };
        }
        if (hideGrid === 'y' || hideGrid === true) {
            yAxes.gridLines = {
                ...yAxes.gridLines,
                display: false,
            };
        }
    }

    if (yStartsAtZero) {
        yAxes.ticks = {
            ...yAxes.ticks,
            beginAtZero: true,
        };
    }

    return (
        <ChartWrapper>
            <Line
                data={{
                    datasets: data.lines.map(chartable => ({
                        backgroundColor: chartable.colour,
                        borderColor: chartable.colour,
                        data: chartable.values,
                        fill: false,
                        label: chartable.label,
                        pointBackgroundColor: 'transparent',
                        pointBorderColor: 'transparent',
                        pointHoverBackgroundColor: chartable.colour,
                        pointHoverBorderColor: chartable.colour,
                    })),
                    labels,
                }}
                legend={{
                    display: false,
                }}
                options={getOptionsForDefaultChartOptions({
                    helpers: {
                        xAxes,
                        yAxes,
                    },
                    options: {
                        elements: {
                            line: {
                                tension: 0,
                            },
                        },
                        ...options,
                        tooltips: {
                            callbacks: {
                                labelColor: item => ({
                                    backgroundColor: data.lines[item.datasetIndex!].colour,
                                    borderColor: data.lines[item.datasetIndex!].colour,
                                }),
                            },
                            intersect: false,
                            mode: 'index',
                        },
                    },
                    otherOptions,
                })}
                {...props}
            />
        </ChartWrapper>
    );
}
TimeLineChart.displayName = 'TimeLineChart';
